import type * as api from '@gitbook/api-client';
import { webConfig } from '@gitbook/env';
import {
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    GitBookLoader,
    Heading,
    toast,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { applySpaceTemplate } from './applySpaceTemplate';
import { SpaceTemplateCard } from './SpaceTemplateCard';
import { TemplateAnalytics } from './TemplateAnalytics';
import { TemplatesList } from './TemplatesList';

type TemplateDialogProps = {
    /**
     * The organization ID.
     */
    organizationId: string;

    /**
     * Callback when the template is applied.
     */
    onComplete: (props: { space: api.Space; changeRequestId?: string }) => void;

    /**
     * The space to apply the template to.
     */
    space?: api.Space | undefined;
} & (
    | {
          createChangeRequest: true;
          changeRequestId?: undefined;
      }
    | {
          createChangeRequest?: undefined;
          changeRequestId?: string;
      }
);

/**
 * Dialog that displays a list of templates and allows the user to preview and apply a template.
 */
export function TemplateDialog(props: TemplateDialogProps) {
    const { organizationId, onComplete, space, createChangeRequest, changeRequestId } = props;
    const [loading, wrapLoading] = useLoadingState();
    const handleApplyTemplate = async (template: api.SpaceTemplate) => {
        toast.catch(async () => {
            const { space: newSpace, changeRequestId: newChangeRequestId } = await wrapLoading(
                applySpaceTemplate({
                    template,
                    organizationId,
                    space,
                    createChangeRequest,
                    changeRequestId,
                })
            );
            await onComplete({ space: newSpace, changeRequestId: newChangeRequestId });
        }, 'Error while applying template');
    };

    return (
        <Dialog size={{ width: 1080 }}>
            <TemplateAnalytics organizationId={organizationId} templateId={null} />
            <DialogHeader>
                <Heading>Pick a template</Heading>
                <Description>Use a template to help you get started.</Description>
            </DialogHeader>
            <DialogBody>
                <TemplatesList>
                    {webConfig.contentTemplates.map((template) => (
                        <SpaceTemplateCard
                            key={template.id}
                            template={template}
                            onApplyTemplate={handleApplyTemplate}
                            organizationId={organizationId}
                        />
                    ))}
                </TemplatesList>
            </DialogBody>

            {loading ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-md bg-base/80">
                    <GitBookLoader />
                    <div className="text-ui-standout">Applying template…</div>
                </div>
            ) : null}
        </Dialog>
    );
}
